import { ChangeDetectionStrategy, Component, ChangeDetectorRef, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, map, take } from 'rxjs/operators';
import { faBullseye } from '@fortawesome/free-solid-svg-icons';
import { BuilderScreenBetaService } from './services/builder-screen.service';
import { SaveComponentsService } from './services/save-components.service';
import { UIStateService } from './services/ui-state.service';
import { ResponsiveLayoutService } from './services/responsive-layout.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { TranslateService } from '@ngx-translate/core';
import { FeatureGuideComponent } from './help/feature-guide.component';
import { PropertyPresetsModalComponent } from './floating-inspector/property-presets-modal.component';
import { AISuggestionsPanelComponent } from './ai-suggestions/ai-suggestions-panel.component';
import { TemplateService } from './services/template.service';
import { ScreenTemplate } from './interfaces/template.models';
import { TemplateApiService } from './services/template-api.service';
import { TabControlService } from './services/tab-control.service';
import { LauncherApiService } from './services/launcher-api.service';
import { CreateLauncherDto, MenuTreeNode } from './interfaces/launcher.models';
import { HTTPService } from 'src/app/services/http.service';
import { DataBindingService } from '../screen-engine/services/data-binding.service';
import { DataSyncService } from '../screen-engine/services/data-sync.service';
import { DataSourceConfig } from './interfaces/data-source.models';
import { LanguageServices } from 'src/app/services/LanguageServices';

@Component({
  selector: 'app-builder-screen-beta',
  templateUrl: './builder-screen.component.html',
  styleUrls: ['./builder-screen.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  // ✅ CORREÇÃO: Fornecer instâncias ISOLADAS dos serviços
  // Garante que o editor não interfira nos launchers e vice-versa
  providers: [
    BuilderScreenBetaService,
    TabControlService,
    SaveComponentsService,
    DataBindingService,
    DataSyncService
  ]
})
export class BuilderScreenBetaComponent implements OnInit, OnDestroy {
  @ViewChild('featureGuide') featureGuide!: FeatureGuideComponent;
  @ViewChild('presetsModal') presetsModal!: PropertyPresetsModalComponent;
  @ViewChild('aiSuggestionsPanel') aiSuggestionsPanel!: AISuggestionsPanelComponent;
  
  private destroy$ = new Subject<void>();
  
  isPaletteCollapsed = false;
  isInspectorCollapsed = false;
  isJsonCollapsed = true;
  showTemplateSelector = false;
  availableTemplates: ScreenTemplate[] = [];
  screenSize$ = this.responsiveLayout.screenSize$;
  layoutConfig$ = this.responsiveLayout.layoutConfig$;
  isPreviewMode$ = this.uiState.workspace$.pipe(map(w => w.previewMode));
  isGridVisible$ = this.uiState.workspace$.pipe(map(w => w.gridVisible));
  zoom$ = this.uiState.workspace$.pipe(map(w => w.zoom));

  // Propriedades legadas para compatibilidade com versões anteriores
  public isSaving = false;
  public hasUnsavedChanges = false;

  // Propriedades para gerenciamento de templates do banco
  showTemplateManagerDrawer = false;
  showTemplateSaveModal = false;
  savedTemplates: ScreenTemplate[] = [];
  loadingTemplates = false;
  currentTemplateId: number | null = null;
  currentTemplateName: string = '';
  currentTemplateDescription: string = '';
  currentTemplateCategory: string = 'custom';
  templateFormData = {
    name: '',
    description: '',
    category: 'custom',
    isPublic: false
  };

  // Propriedades para criação de launcher
  showLauncherModal = false;
  launcherFormData: CreateLauncherDto = {
    idTemplate: 0,
    name: '',
    description: '',
    routePath: '',
    iconName: 'layout',
    parentMenuId: null,
    menuOrder: 0
  };
  menuTreeData: MenuTreeNode[] = [];
  loadingMenuTree = false;
  customIcons: Array<{name: string, displayName: string, isCustom: boolean}> = [];
  loadingCustomIcons = false;

  // Propriedades para configuração de data source
  showDataSourceConfigModal = false;
  currentDataSourceConfig: DataSourceConfig = { type: 'none' };

  constructor(
    private readonly ls: LanguageServices,
    public readonly builderService: BuilderScreenBetaService,
    private readonly saveService: SaveComponentsService,
    public readonly uiState: UIStateService, // Tornado público para acesso do template
    private readonly responsiveLayout: ResponsiveLayoutService,
    private readonly message: NzMessageService,
    private readonly modal: NzModalService,
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef,
    public readonly templateService: TemplateService,
    private readonly templateApi: TemplateApiService,
    private readonly launcherApi: LauncherApiService,
    private readonly http: HTTPService
  ) {}

  ngOnInit() {
    // Carrega os templates disponíveis (locais e do banco)
    this.loadTemplates();
    this.loadSavedTemplatesFromDatabase();

    this.responsiveLayout.screenSize$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(screenSize => {
      this.uiState.updateWorkspace({ screenSize });
    });

    // Conecta a seleção do builder service ao UI state service para o inspetor flutuante
    this.builderService.selectedId$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(selectedId => {
      this.uiState.selectComponent(selectedId);
    });

    // Subscreve ao estado do builder service para compatibilidade legada
    this.builderService.isSaving$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(isSaving => {
      this.isSaving = isSaving;
      this.cdr.markForCheck();
    });

    this.builderService.hasUnsavedChanges$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(hasChanges => {
      this.hasUnsavedChanges = hasChanges;
      this.cdr.markForCheck();
    });
  }

  /**
   * Carrega os templates disponíveis
   */
  private loadTemplates(): void {
    try {
      this.availableTemplates = this.templateService.getTemplatesSync();
      
      // Forçar detecção de mudanças
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      this.availableTemplates = [];
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }


  /**
   * Aplica um template ao canvas
   */
  onTemplateApplied(template: ScreenTemplate): void {
    try {
      this.builderService.applyTemplate(template);
      this.message.success(this.ls.instant('builderScreen.msg.template.applied', { name: template.name }));
      this.showTemplateSelector = false; // Fecha o modal após aplicação
    } catch (error) {
      console.error('Erro ao aplicar template:', error);
      this.message.error(this.ls.instant('builderScreen.msg.template.applyError', { error: error instanceof Error ? error.message : this.ls.instant('builderScreen.msg.unknownError') }));
    }
  }

  /**
   * Abre o seletor de templates
   */
  openTemplateSelector(): void {
    // Se não temos templates, tentar carregar novamente
    if (!this.availableTemplates || this.availableTemplates.length === 0) {
      this.loadTemplates();
    }
    
    this.showTemplateSelector = true;
    
    // Forçar detecção de mudanças
    this.cdr.detectChanges();
  }

  /**
   * Fecha o seletor de templates
   */
  onTemplateSelectorClose(): void {
    this.showTemplateSelector = false;
  }

  /**
   * Alterna a exibição do seletor de templates inline
   */
  toggleTemplateSelector(): void {
    this.showTemplateSelector = !this.showTemplateSelector;
    
    // Se estamos abrindo, garantir que os templates estão carregados
    if (this.showTemplateSelector && (!this.availableTemplates || this.availableTemplates.length === 0)) {
      this.loadTemplates();
    }
  }

  onSaveClick(): void {
    if (this.isSaving) return;

    this.isSaving = true;
    this.cdr.markForCheck();


    const componentData = this.saveService.collectComponentValues();

    if (componentData.length === 0) {
      this.translate.get('builderScreen.save.noData').subscribe((msg) => {
        this.message.warning(msg);
        this.isSaving = false;
        this.cdr.markForCheck();
      });
      return;
    }


    const validation = this.saveService.validateRequiredFields(componentData);

    if (!validation.isValid) {
      this.translate.get('builderScreen.save.validationError').subscribe((msg) => {
        this.message.error(`${msg}: ${validation.errors.join(', ')}`);
        this.isSaving = false;
        this.cdr.markForCheck();
      });
      return;
    }


    this.saveService.saveFormData(componentData).subscribe({
      next: (result) => {
        if (result.success) {
          this.translate.get('builderScreen.save.success').subscribe((msg) => {
            this.message.success(msg);
            this.hasUnsavedChanges = false;
            this.isSaving = false;
            this.cdr.markForCheck();
          });
        }
      },
      error: (error) => {
        console.error('Save error:', error);
        this.translate.get('builderScreen.save.error').subscribe((msg) => {
          this.message.error(msg);
          this.isSaving = false;
          this.cdr.markForCheck();
        });
      },
    });
  }


  onCancelClick(): void {
    if (this.hasUnsavedChanges) {
      this.modal.confirm({
        nzTitle: this.translate.instant('builderScreen.cancel.confirmTitle'),
        nzContent: this.translate.instant('builderScreen.cancel.confirmMessage'),
        nzOkText: this.translate.instant('builderScreen.cancel.confirm'),
        nzCancelText: this.translate.instant('builderScreen.cancel.keep'),
        nzOnOk: () => {
          this.resetForm();
        },
      });
    } else {
      this.resetForm();
    }
  }

  private resetForm(): void {
    // Reseta todos os valores do formulário (implementação placeholder - numa implementação real
    // você pode querer resetar o estado do builder)
    this.hasUnsavedChanges = false;
    this.translate.get('builderScreen.cancel.success').subscribe((msg) => {
      this.message.info(msg);
      this.cdr.markForCheck();
    });
  }


  onComponentSelected(componentId: string) {
    this.uiState.selectComponent(componentId);
  }

  togglePalette() {
    this.isPaletteCollapsed = !this.isPaletteCollapsed;
  }

  toggleInspector() {
    this.isInspectorCollapsed = !this.isInspectorCollapsed;
  }

  toggleJson() {
    this.isJsonCollapsed = !this.isJsonCollapsed;
  }

  toggleGrid() {
    const currentGridVisible = this.uiState.getCurrentState().workspace.gridVisible;
    this.uiState.updateWorkspace({ gridVisible: !currentGridVisible });
  }

  togglePreview() {
    const currentPreviewMode = this.uiState.getCurrentState().workspace.previewMode;
    this.uiState.updateWorkspace({ previewMode: !currentPreviewMode });
  }

  onShowHelp() {
    if (this.featureGuide) {
      this.featureGuide.show();
    }
  }

  onShowPresets() {
    if (this.presetsModal) {
      this.presetsModal.show();
    }
  }

  onShowAISuggestions() {
    if (this.aiSuggestionsPanel) {
      this.aiSuggestionsPanel.show();
    }
  }

  onCloseDocked() {
    this.uiState.hideInspector();
  }

  /**
   * Carrega templates salvos do banco de dados
   */
  loadSavedTemplatesFromDatabase(): void {
    this.loadingTemplates = true;
    this.templateApi.getTemplates().subscribe({
      next: (templates) => {
        this.savedTemplates = templates;
        this.loadingTemplates = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Erro ao carregar templates do banco:', error);
        this.loadingTemplates = false;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Abre o drawer de gerenciamento de templates
   */
  openTemplateManager(): void {
    this.loadSavedTemplatesFromDatabase();
    this.showTemplateManagerDrawer = true;
  }

  /**
   * Fecha o drawer de gerenciamento
   */
  closeTemplateManager(): void {
    this.showTemplateManagerDrawer = false;
  }

  /**
   * Carrega um template salvo no canvas
   */
  loadSavedTemplate(template: ScreenTemplate): void {
    try {
      this.builderService.applyTemplate(template);
      // Usar o ID numérico do banco (idTemplate), não o ID textual (id)
      this.currentTemplateId = template.idTemplate || null;
      this.currentTemplateName = template.name;
      this.currentTemplateDescription = template.description;
      this.currentTemplateCategory = template.category;
      this.message.success(this.ls.instant('builderScreen.msg.template.loaded', { name: template.name }));
      this.showTemplateManagerDrawer = false;
      this.cdr.markForCheck();
    } catch (error) {
      console.error('Erro ao carregar template:', error);
      this.message.error(this.ls.instant('builderScreen.msg.template.loadError'));
    }
  }

  /**
   * Abre modal para salvar template (novo ou atualizar existente)
   */
  openSaveTemplateModal(): void {
    // Se há um template carregado, preencher com seus dados
    if (this.currentTemplateId) {
      this.templateFormData = {
        name: this.currentTemplateName,
        description: this.currentTemplateDescription,
        category: this.currentTemplateCategory,
        isPublic: false
      };
    } else {
      // Novo template - limpar formulário
      this.templateFormData = {
        name: '',
        description: '',
        category: 'custom',
        isPublic: false
      };
    }
    this.showTemplateSaveModal = true;
  }

  /**
   * Salva o template atual no banco
   * ✅ CORRIGIDO: Agora salva o JSON completo (tabs/panels/fields) ao invés de apenas components
   */
  onSaveTemplate(): void {
    if (!this.validateTemplateData()) {
      return;
    }

    // ✅ CORREÇÃO: Obter o JSON COMPLETO do builderService ao invés de tentar extrair components dos nodes
    this.builderService.jsonOutput$.pipe(take(1)).subscribe(jsonOutput => {
      

      // ✅ Criar template com estrutura JSON completa
      const template: any = {
        id: '',
        name: this.templateFormData.name,
        description: this.templateFormData.description,
        category: this.templateFormData.category as any,
        gridConfig: {
          columns: 24,
          rows: 15,
          gap: '16px'
        },
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // ✅ Se o JSON tem estrutura completa (tabs/panels/fields), adicionar ao template
      if ((jsonOutput as any).tabs && (jsonOutput as any).panels && (jsonOutput as any).fields) {
        
        template.tabs = (jsonOutput as any).tabs;
        template.panels = (jsonOutput as any).panels;
        template.fields = (jsonOutput as any).fields;
        template.rows = (jsonOutput as any).rows || []; //  CRÍTICO: Salvar array de Rows
        // Barra de Contexto: salvar contextFields no template
        if ((jsonOutput as any).contextFields) {
          template.contextFields = (jsonOutput as any).contextFields;
        }
        template.components = []; // Manter vazio para compatibilidade
        
        
      } else {
        // Fallback: formato antigo com components (não deveria acontecer mais)
        console.warn('⚠️ JSON output does not have tabs/panels/fields, using legacy format');
        const currentState = this.builderService.currentState;
        const components = currentState.nodes
          .filter(n => n.type !== 'tabs')
          .map(node => ({
            type: node.type,
            props: node.props,
            position: {
              row: (node.props as any).row || 0,
              col: (node.props as any).col || 0
            }
          }));
        template.components = components as any;
      }

      if (this.currentTemplateId) {
        // Atualizar template existente
        this.templateApi.updateTemplate(this.currentTemplateId, template).subscribe({
          next: () => {
            // Atualizar dados do template atual
            this.currentTemplateName = this.templateFormData.name;
            this.currentTemplateDescription = this.templateFormData.description;
            this.currentTemplateCategory = this.templateFormData.category;
            
            this.message.success(this.ls.instant('builderScreen.msg.template.updated'));
            this.showTemplateSaveModal = false;
            this.loadSavedTemplatesFromDatabase();
            this.cdr.markForCheck();
          },
          error: (error) => {
            this.message.error(this.ls.instant('builderScreen.msg.template.updateError'));
          }
        });
      } else {
        // Criar novo template
        this.templateApi.createTemplate(template, this.templateFormData.isPublic).subscribe({
          next: (newId) => {
            // Armazenar dados do novo template
            this.currentTemplateId = newId;
            this.currentTemplateName = this.templateFormData.name;
            this.currentTemplateDescription = this.templateFormData.description;
            this.currentTemplateCategory = this.templateFormData.category;
            
            this.message.success(this.ls.instant('builderScreen.msg.template.saved'));
            this.showTemplateSaveModal = false;
            this.loadSavedTemplatesFromDatabase();
            this.cdr.markForCheck();
          },
          error: (error) => {
            this.message.error(this.ls.instant('builderScreen.msg.template.saveError'));
          }
        });
      }
    }); // ✅ Fechamento do subscribe de jsonOutput$
  }

  /**
   * Valida dados do formulário
   */
  private validateTemplateData(): boolean {
    if (!this.templateFormData.name || this.templateFormData.name.trim() === '') {
      this.message.warning(this.ls.instant('builderScreen.msg.template.nameRequired'));
      return false;
    }
    if (!this.templateFormData.category) {
      this.message.warning(this.ls.instant('builderScreen.msg.template.categoryRequired'));
      return false;
    }
    return true;
  }

  /**
   * Cria um novo template (limpa o canvas)
   */
  createNewTemplate(): void {
    this.modal.confirm({
      nzTitle: 'Criar novo template?',
      nzContent: 'Isso irá limpar o canvas atual. Deseja continuar?',
      nzOkText: 'Sim, criar novo',
      nzCancelText: 'Cancelar',
      nzOnOk: () => {
        this.builderService.clearCanvas();
        // Limpar dados do template atual
        this.currentTemplateId = null;
        this.currentTemplateName = '';
        this.currentTemplateDescription = '';
        this.currentTemplateCategory = 'custom';
        this.message.info(this.ls.instant('builderScreen.msg.canvas.cleared'));
        this.showTemplateManagerDrawer = false;
      }
    });
  }

  /**
   * Exclui um template salvo
   */
  deleteSavedTemplate(idTemplate: number, templateName: string): void {
    this.modal.confirm({
      nzTitle: 'Confirmar exclusão',
      nzContent: `Deseja realmente excluir o template "${templateName}"?`,
      nzOkText: 'Excluir',
      nzOkDanger: true,
      nzCancelText: 'Cancelar',
      nzOnOk: () => {
        this.templateApi.deleteTemplate(idTemplate).subscribe({
          next: () => {
            this.message.success(this.ls.instant('builderScreen.msg.template.deleted'));
            if (this.currentTemplateId === idTemplate) {
              this.currentTemplateId = null;
            }
            this.loadSavedTemplatesFromDatabase();
          },
          error: (error) => {
            this.message.error(this.ls.instant('builderScreen.msg.template.deleteError'));
          }
        });
      }
    });
  }

  /**
   *  NOVO: Handler para deletar template via template-selector
   */
  handleTemplateDelete(templateId: number): void {
    const template = this.savedTemplates.find(t => t.idTemplate === templateId);
    if (!template) return;
    
    // Reutilizar o método existente deleteSavedTemplate
    this.deleteSavedTemplate(templateId, template.name);
  }

  /**
   * Handler para duplicar template via template-selector
   */
  handleTemplateDuplicate(data: { idTemplate: number; newName: string }): void {
    this.templateApi.duplicateTemplate(data.idTemplate, data.newName).subscribe({
      next: (newTemplateId) => {
        this.message.success(this.ls.instant('builderScreen.msg.template.duplicated', { id: newTemplateId }));
        // Recarregar lista de templates
        this.loadSavedTemplatesFromDatabase();
      },
      error: (error) => {
        console.error('Erro ao duplicar template:', error);
        this.message.error(this.ls.instant('builderScreen.msg.template.duplicateError'));
      }
    });
  }

  /**
   * Retorna cor da categoria
   */
  getCategoryColor(category: string): string {
    const colors: { [key: string]: string } = {
      product: 'blue',
      partner: 'green',
      marketplace: 'orange',
      custom: 'purple'
    };
    return colors[category] || 'default';
  }

  /**
   * Retorna nome da categoria
   */
  getCategoryName(category: string): string {
    const names: { [key: string]: string } = {
      product: 'Produtos',
      partner: 'Parceiros',
      marketplace: 'Marketplaces',
      custom: 'Personalizado'
    };
    return names[category] || category;
  }

  // ==========================================
  // Métodos para Criação de Launcher
  // ==========================================

  /**
   * Abre modal de criação de launcher
   */
  openLauncherModal(): void {
    if (!this.currentTemplateId) {
      this.message.warning(this.ls.instant('builderScreen.msg.launcher.saveTemplateFirst'));
      return;
    }
    
    // Preencher dados iniciais
    this.launcherFormData = {
      idTemplate: this.currentTemplateId,
      name: this.currentTemplateName || '',
      description: this.currentTemplateDescription || '',
      routePath: this.sanitizeRoutePath(this.currentTemplateName || ''),
      iconName: 'layout',
      parentMenuId: null,
      menuOrder: 0
    };
    
    this.loadMenuTree();
    this.loadCustomIcons();
    this.showLauncherModal = true;
  }

  /**
   * Carrega árvore de menus para seleção do menu pai
   */
  loadMenuTree(): void {
    this.loadingMenuTree = true;
    
    const query = {
      entityName: 'Mktpmenu',
      sql: `SELECT NUMENU, IDMENU, DESCRIPTION 
            FROM MKTPMENU 
            WHERE ENABLE = 'S' AND TYPE = 'R' 
            ORDER BY DESCRIPTION`
    };
    
    this.http.post(query, 'JsonBrokerServices/findnative', false, true, true).subscribe({
      next: (response: any) => {
        if (response && response.SQL) {
          this.menuTreeData = response.SQL.map((m: any) => ({
            title: m.DESCRIPTION,
            key: m.IDMENU,
            isLeaf: false
          }));
        }
        this.loadingMenuTree = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Erro ao carregar menu tree:', error);
        this.loadingMenuTree = false;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Carrega ícones customizados da tabela MKTPICONS
   * Os ícones já foram carregados no login via UserAuthenticationServices.loadSvgs()
   * que registra com NzIconService.addIconLiteral("custom:" + svgName, svg)
   */
  loadCustomIcons(): void {
    this.loadingCustomIcons = true;
    
    const query = {
      entityName: 'Mktpicons',
      sql: `SELECT NAME FROM MKTPICONS ORDER BY NAME`
    };
    
    this.http.post(query, 'JsonBrokerServices/findnative', false, true, true).subscribe({
      next: (response: any) => {
        if (response && response.SQL) {
          // nz-icon usa o nome COM prefixo "custom:" (registrado no NzIconService em loadSvgs)
          this.customIcons = response.SQL.map((icon: any) => {
            const originalName = icon.NAME;

            return {
              name: `custom:${originalName}`, // Salvar no banco com prefixo para identificação (e nzType do nz-icon)
              displayName: originalName,       // Nome para display
              svgIconName: originalName,       // Nome original SEM prefixo
              isCustom: true
            };
          });
        }
        this.loadingCustomIcons = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Erro ao carregar ícones customizados:', error);
        this.loadingCustomIcons = false;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Abre modal de configuração de data source
   */
  openDataSourceConfig(): void {
    this.showDataSourceConfigModal = true;
  }

  /**
   * Handler para mudança de configuração de data source
   */
  handleDataSourceConfigChange(config: DataSourceConfig): void {
    this.currentDataSourceConfig = config;
  
  }

  /**
   * Retorna label descritiva do data source atual
   */
  getDataSourceLabel(): string {
    switch (this.currentDataSourceConfig.type) {
      case 'formula':
        return 'Fórmula configurada';
      case 'mock':
        return 'Mock JSON configurado';
      case 'api':
        return 'API configurada';
      default:
        return 'Nenhum data source';
    }
  }

  /**
   * Retorna cor do tag baseado no tipo de data source
   */
  getDataSourceColor(): string {
    switch (this.currentDataSourceConfig.type) {
      case 'formula':
        return 'blue';
      case 'mock':
        return 'orange';
      case 'api':
        return 'green';
      default:
        return 'default';
    }
  }

  /**
   * Cria launcher de tela
   */
  onCreateLauncher(): void {
    if (!this.validateLauncherData()) {
      return;
    }
    
    // Sanitizar routePath
    this.launcherFormData.routePath = this.sanitizeRoutePath(this.launcherFormData.routePath);
    
    // Adicionar configuração de data source
    this.launcherFormData.dataSourceConfig = this.currentDataSourceConfig;
    
    this.launcherApi.createLauncher(this.launcherFormData).subscribe({
      next: (idLauncher) => {
        this.message.success(
          'Lançador criado com sucesso! Recarregue a página (F5) para ver o novo menu.',
          { nzDuration: 5000 }
        );
        this.showLauncherModal = false;
        // Resetar data source config
        this.currentDataSourceConfig = { type: 'none' };
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Erro ao criar launcher:', error);
        this.message.error(this.ls.instant('builderScreen.msg.launcher.createError'));
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Valida dados do launcher antes de salvar
   */
  private validateLauncherData(): boolean {
    if (!this.launcherFormData.name || this.launcherFormData.name.trim() === '') {
      this.message.warning(this.ls.instant('builderScreen.msg.launcher.nameRequired'));
      return false;
    }
    
    if (!this.launcherFormData.routePath || this.launcherFormData.routePath.trim() === '') {
      this.message.warning(this.ls.instant('builderScreen.msg.launcher.routeRequired'));
      return false;
    }
    
    // Validar formato do routePath
    const routePathRegex = /^[a-z0-9-]+$/;
    if (!routePathRegex.test(this.launcherFormData.routePath)) {
      this.message.warning(this.ls.instant('builderScreen.msg.launcher.routeInvalid'));
      return false;
    }
    
    return true;
  }

  /**
   * Sanitiza string para formato de rota válido
   */
  private sanitizeRoutePath(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[^a-z0-9]/g, '-') // Substitui especiais por hífen
      .replace(/-+/g, '-') // Remove hífens duplicados
      .replace(/^-|-$/g, ''); // Remove hífens início/fim
  }

  /**
   * Atualiza routePath automaticamente quando o nome muda
   */
  onLauncherNameChange(): void {
    if (this.launcherFormData.name) {
      this.launcherFormData.routePath = this.sanitizeRoutePath(this.launcherFormData.name);
      this.cdr.markForCheck();
    }
  }

  // ===== Auto Screen Generator =====

  autoScreenGeneratorVisible = false;

  /**
   * Abre o Auto Screen Generator
   */
  openAutoScreenGenerator(): void {
    this.autoScreenGeneratorVisible = true;
  }

  /**
   * Callback quando tela é gerada
   */
  onScreenGenerated(result: any): void {
    

    try {
      //  NOVO: Passar config para convertAnalysisToBuilderJson
      const builderJson = this.convertAnalysisToBuilderJson(result, result.config);
      
    
      
      // Limpar canvas atual
      this.builderService.clearCanvas();
      
      // Aplicar nova estrutura
      this.builderService.loadFromJson(builderJson);
      
      this.message.success(this.ls.instant('builderScreen.msg.generate.success', { count: result.entities.length }));
      
      // Fechar modal
      this.autoScreenGeneratorVisible = false;
    } catch (error) {
      console.error('❌ Erro ao aplicar tela gerada:', error);
      this.message.error(this.ls.instant('builderScreen.msg.generate.error', { error: (error as Error).message }));
    }
  }

  // ===== Optimized Auto Screen Generator =====

  optimizedAutoScreenGeneratorVisible = false;

  /**
   * ⚡ NOVO: Abre o Gerador Otimizado de Telas
   */
  openOptimizedAutoScreenGenerator(): void {
    this.optimizedAutoScreenGeneratorVisible = true;
  }

  /**
   * ⚡ NOVO: Callback quando tela otimizada é gerada
   */
  onOptimizedScreenGenerated(result: any): void {
    

    try {
      // Converter análise otimizada para formato do builder
      const builderJson = this.convertOptimizedAnalysisToBuilderJson(result, result.config);
          
      
      // Limpar canvas atual
      this.builderService.clearCanvas();
      
      // Aplicar nova estrutura
      this.builderService.loadFromJson(builderJson);
      
      this.message.success(this.ls.instant('builderScreen.msg.generate.optimizedSuccess', { count: result.entities.length, strategy: result.metadata.suggestedStrategy }), 
        { nzDuration: 4000 });
      
      // Fechar modal
      this.optimizedAutoScreenGeneratorVisible = false;
    } catch (error) {
      console.error('❌ Erro ao aplicar tela otimizada:', error);
      this.message.error(this.ls.instant('builderScreen.msg.generate.optimizedError', { error: (error as Error).message }));
    }
  }

  /**
   * ⚡ NOVO: Converte resultado da análise OTIMIZADA para formato JSON do Builder
   * Similar ao convertAnalysisToBuilderJson mas adaptado para o resultado otimizado
   */
  private convertOptimizedAnalysisToBuilderJson(result: any, config?: any): any {
    const tabs: any[] = [];
    const panels: any[] = [];
    const fields: any[] = [];

    // Usar o layout sugerido pela análise otimizada
    const suggestedLayout = result.suggestedLayout;

    // 1. Criar tabs do layout otimizado
    tabs.push(...suggestedLayout.tabs.map((t: any) => ({
      id: t.id,
      name: t.name,
      icon: 'folder',
      themeIcon: 'outline',
      twotoneColor: '#1890ff',
      disabled: false
    })));

    // 2. Criar panels do layout otimizado
    panels.push(...suggestedLayout.panels.map((p: any) => ({
      id: p.id,
      name: p.name,
      active: true,
      disabled: false,
      tab: p.tab,
      order: p.order,
      collapsible: config?.collapseComplexFields || true,
      collapsed: false
    })));

    

    // 3. Criar DataGrids para cada entidade
    let fieldIndex = 0;

    result.entities.forEach((entity: any, entityIndex: number) => {
      const entityName = entity.name.toLowerCase();
      
      // Verificar se entidade foi selecionada para geração
      if (config?.selectedEntities && !config.selectedEntities.includes(entity.name)) {        
        return;
      }

      // Verificar se tem colunas
      if (!entity.suggestedColumns || entity.suggestedColumns.length === 0) {        
        return;
      }

      //  FIX: Encontrar o panel correto comparando pelo índice da entidade no array original
      // Os panels foram criados na mesma ordem que as entidades pelo OptimizedScreenAnalyzerService
      const entityGlobalIndex = result.entities.indexOf(entity);
      
      let entityPanel = suggestedLayout.panels.find((p: any) => {
        // Match pelo padrão do ID: tab_NOME_INDICE_grid
        const expectedPanelPattern = `tab_${entityName}_`;
        if (p.id.startsWith(expectedPanelPattern) && p.id.endsWith('_grid')) {
          // Extrair o índice do panel
          const panelIndexMatch = p.id.match(/_(\d+)_grid$/);
          if (panelIndexMatch) {
            const panelEntityIndex = parseInt(panelIndexMatch[1]);
            
            // Para multi-tab, os índices dos panels level-1 correspondem à posição deles
            // no array de entidades level-1 com suggestedGrouping === 'own-tab'
            const level1Entities = result.entities.filter((e: any) => 
              e.level === 1 && (e.suggestedGrouping === 'own-tab' || e.importance === 'high')
            );
            const entityPositionInLevel1 = level1Entities.indexOf(entity);
            
            // Se a entidade está no nível 1, comparar com o índice do panel
            if (entityPositionInLevel1 >= 0) {
              return panelEntityIndex === entityPositionInLevel1;
            }
          }
        }
        return false;
      });
      
      // Fallback: se não encontrou pelo índice, buscar pelo nome (comportamento antigo)
      if (!entityPanel) {
        entityPanel = suggestedLayout.panels.find((p: any) => 
          p.name.toLowerCase().includes(entityName) || 
          p.id.toLowerCase().includes(entityName)
        );
      }
      
      const panelId = entityPanel?.id || panels[0]?.id;
      
      

      // Gerar label legível se configurado
      let gridLabel = entity.name;
      if (config?.generateReadableLabels && entity.isPrimitiveArray && entity.parentContext) {
        const formattedContext = this.formatLabel(entity.parentContext);
        const formattedField = this.formatLabel(entity.originalFieldName?.replace('_list', '') || entity.name);
        gridLabel = `${formattedContext} - ${formattedField}`;
      }

      

      // Determinar se criar formulário
      let formPanelId: string | undefined = undefined;
      const formFields: any[] = [];

      if (config?.autoGenerateForms && !entity.isPrimitiveArray) {
        //  FIX: Usar posição dentro do array level-1 (mesma lógica do panel matching)
        const level1Entities = result.entities.filter((e: any) => 
          e.level === 1 && (e.suggestedGrouping === 'own-tab' || e.importance === 'high')
        );
        const entityPositionInLevel1 = level1Entities.indexOf(entity);
        const formPanelIndex = entityPositionInLevel1 >= 0 ? entityPositionInLevel1 : entityGlobalIndex;
        
        formPanelId = `form_panel_${entityName}_${formPanelIndex}`;
        const formPanelName = `Formulário - ${entity.name}`;
        
        // Encontrar a tab correta (já usa o panelId correto do fix anterior)
        const gridPanel = panels.find(p => p.id === panelId);
        const tabId = gridPanel?.tab || tabs[0]?.id;
        
        // Criar painel de formulário
        panels.push({
          id: formPanelId,
          name: formPanelName,
          active: true,
          disabled: false,
          tab: tabId,
          order: panels.length + 1,
          collapsible: true,
          collapsed: entity.complexity >= 7 && config?.collapseComplexFields
        });

        

        // Criar campos do formulário
        let formRow = 0;
        entity.suggestedColumns.forEach((column: any, colIndex: number) => {
          if (column.field === 'id' && colIndex === 0) return;

          const componentType = this.mapColumnTypeToFormType(column.type);
          //  FIX: Usar formPanelIndex (já calculado acima)
          const fieldId = `form_field_${entityName}_${column.field}_${formPanelIndex}_${colIndex}`;

          formFields.push({
            field: fieldId,
            type: componentType,
            label: column.header,
            panel: formPanelId,
            col: 0,
            row: formRow++,
            colSpan: componentType === 'textarea' ? 24 : 12,
            required: false,
            placeholder: `Digite ${column.header}...`,
            isDisabled: false,
            hidden: false,
            dataPath: column.dataPath,
            ...(componentType === 'number' && { min: null, max: null }),
            ...(componentType === 'switch' && { 
              defaultValue: false,
              checkedChildren: 'Sim',
              unCheckedChildren: 'Não'
            }),
            ...(componentType === 'text' && { maxLength: 255 })
          });
        });

        fields.push(...formFields);
        
      }

      // Criar o DataGrid field
      //  FIX: Usar a mesma lógica de índice que o formPanelIndex
      const level1EntitiesForGrid = result.entities.filter((e: any) => 
        e.level === 1 && (e.suggestedGrouping === 'own-tab' || e.importance === 'high')
      );
      const entityPositionInLevel1ForGrid = level1EntitiesForGrid.indexOf(entity);
      const gridFieldIndex = entityPositionInLevel1ForGrid >= 0 ? entityPositionInLevel1ForGrid : entityGlobalIndex;
      const gridFieldId = `grid_${entityName}_${Date.now()}_${gridFieldIndex}`;
      
      fields.push({
        field: gridFieldId,
        type: 'datagrid',
        label: gridLabel,
        panel: panelId,
        col: 0,
        row: fieldIndex++,
        colSpan: 24,
        required: false,
        isDisabled: false,
        hidden: false,
        dataPath: entity.dataPath,
        columns: entity.suggestedColumns,
        enablePagination: config?.enablePagination !== false,
        pageSize: config?.pageSize || 10,
        enableFilters: config?.enableFilters !== false,
        enableSorting: config?.enableSorting !== false,
        enableCRUD: config?.enableCRUD !== false,
        selectionMode: 'single',
        enableMasterDetail: config?.enableMasterDetail !== false && formPanelId !== undefined,
        masterDetailFormPanel: formPanelId,
        masterDetailAutoGenerate: false
      });

      
    });



    return { tabs, panels, fields };
  }

  /**
   *  REESCRITO COMPLETAMENTE: Converte ScreenAnalysisResult para formato JSON do Builder
   * Estratégia: APENAS 1 DataGrid por entidade, sem duplicações
   *  NOVO: Suporta geração automática de formulários Master-Detail
   */
  private convertAnalysisToBuilderJson(result: any, config?: any): any {
    const tabs: any[] = [];
    const panels: any[] = [];
    const fields: any[] = [];


    // Usar o layout sugerido pelo analyzer
    const suggestedLayout = result.suggestedLayout;

    // 1. Criar tabs conforme sugerido
    tabs.push(...suggestedLayout.tabs.map((t: any) => ({
      id: t.id,
      name: t.name,
      icon: 'folder',
      themeIcon: 'outline',
      twotoneColor: '#1890ff',
      disabled: false
    })));

    // 2. Criar panels conforme sugerido
    panels.push(...suggestedLayout.panels.map((p: any, idx: number) => ({
      id: p.id,
      name: p.name,
      active: true,
      disabled: false,
      tab: p.tab,
      order: idx + 1
    })));

    

    // 3. Criar DataGrids hierarquicamente
    // Agrupar por panel para empilhar corretamente (row 0, 1, 2...)
    const gridsByPanel: Map<string, number> = new Map(); // Conta quantos grids já foram adicionados em cada panel

    result.entities.forEach((entity: any, entityIndex: number) => {
      const entityName = entity.name.toLowerCase();
      const entityLevel = entity.level || 0;

      // Verificar se tem colunas para criar o DataGrid
      if (!entity.suggestedColumns || entity.suggestedColumns.length === 0) {
        console.warn(`  ⚠️ Entidade "${entity.name}" sem colunas, pulando...`);
        return;
      }

      //  IMPORTANTE: Determinar o panel correto baseado no nível E tipo
      const rootEntityName = result.entities[0].name.toLowerCase();
      const isPrimitiveArray = entity.isPrimitiveArray || false;
      let panelId: string;
      
      if (entityLevel === 0) {
        // Entidade raiz (nível 0)
        panelId = `tab_${entityName}_main_grid`;
      } else if (entityLevel === 1 && !isPrimitiveArray) {
        // Entidade de nível 1 (filha direta da raiz) E não é array de primitivos - tem tab própria
        panelId = `tab_${rootEntityName}_${entityName}_grid`;
      } else {
        //  Arrays de primitivos OU entidades de nível 2+ vão para o panel apropriado
        if (isPrimitiveArray) {
          // Arrays de primitivos sempre vão para o panel da entidade raiz (Item_list)
          panelId = `tab_${rootEntityName}_main_grid`;
          
        } else {
          // Entidades de nível 2+ (netos, bisnetos) - vai para o panel do PAI (nível 1)
          const parentEntity = result.entities.find((e: any) => 
            e.level === 1 && !e.isPrimitiveArray && entity.dataPath.includes(e.dataPath)
          );
          
          if (parentEntity) {
            panelId = `tab_${rootEntityName}_${parentEntity.name.toLowerCase()}_grid`;
          
          } else {
            // Fallback: usar panel da raiz
            panelId = `tab_${rootEntityName}_main_grid`;
            console.warn(`  ⚠️ Pai não encontrado para "${entity.name}", usando panel raiz`);
          }
        }
      }

      // Validar se o panel existe no layout
      const panelExists = suggestedLayout.panels.some((p: any) => p.id === panelId);
      if (!panelExists) {
        console.warn(`  ⚠️ Panel "${panelId}" não encontrado no layout!`);
        panelId = suggestedLayout.panels[Math.min(entityIndex, suggestedLayout.panels.length - 1)]?.id || panelId;
      }

      //  Calcular row baseado em quantos grids já foram adicionados neste panel
      const currentRow = gridsByPanel.get(panelId) || 0;
      gridsByPanel.set(panelId, currentRow + 1);

      //  Gerar label com contexto do pai para evitar duplicação
      let gridLabel = entity.name;
      if (isPrimitiveArray && entity.parentContext) {
        // Ex: "Image_id_list" + contexto "promotion_image" = "Promotion Image - Image Id List"
        const formattedContext = this.formatLabel(entity.parentContext);
        const formattedField = this.formatLabel(entity.originalFieldName?.replace('_list', '') || entity.name);
        gridLabel = `${formattedContext} - ${formattedField}`;
      }

      

      //  ISOLAMENTO COMPLETO DE FUNCIONALIDADES:
      // 1. autoGenerateForms → Cria painéis de formulário com campos no canvas
      // 2. enableMasterDetail → Apenas habilita edição (sem criar painéis)
      let formPanelId: string | undefined = undefined;
      const formFields: any[] = []; //  Array temporário para campos do formulário
      
      //  Criar painéis e campos APENAS se autoGenerateForms estiver ativo
      if (config?.autoGenerateForms) {
        //  ID único para cada panel de formulário usando entityName e entityIndex
        formPanelId = `form_panel_${entityName}_${entityIndex}`;
        const formPanelName = `Formulário - ${entity.name}`;
        
        // Encontrar a tab correta para este panel de formulário
        const gridPanel = suggestedLayout.panels.find((p: any) => p.id === panelId);
        const tabId = gridPanel?.tab || suggestedLayout.tabs[0]?.id;
        
        //  Criar painel no canvas
        panels.push({
          id: formPanelId,
          name: formPanelName,
          active: true,
          disabled: false,
          tab: tabId,
          order: panels.length + 1
        });

        

        //  Criar campos do formulário
        let formRow = 0;
        entity.suggestedColumns.forEach((column: any, colIndex: number) => {
          // Pular coluna 'id' se for a primeira
          if (column.field === 'id' && colIndex === 0) {
            return;
          }

          // Determinar tipo de componente
          const componentType = this.mapColumnTypeToFormType(column.type);
          
          //  CRÍTICO: dataPath do campo do formulário = FIELD da coluna (não dataPath)
          // Porque os dados da linha da grid usam column.field como chave, não column.dataPath
          const fieldDataPath = column.field;
          
          // Criar campo de formulário com TODAS as props necessárias
          const formField = {
            field: `form_field_${entityName}_${column.field}_${entityIndex}_${colIndex}`,
            type: componentType,
            label: column.header || this.formatLabel(column.field),
            placeholder: `Digite ${column.header || column.field}...`,
            panel: formPanelId,
            col: 0,
            row: formRow++,
            colSpan: componentType === 'textarea' ? 24 : 12,
            required: false,
            dataPath: fieldDataPath, //  Usa column.field para mapear com os dados da linha
            isDisabled: false,
            hidden: false,
            ...this.getDefaultPropsForFormType(componentType)
          };

          formFields.push(formField);
          
        });

        
      }

      //  IMPORTANTE: Criar o DataGrid com props Master-Detail independente dos formulários
      const gridField: any = {
        field: `grid_${entityName}_${Date.now()}_${entityIndex}`,
        type: 'datagrid',
        label: gridLabel, //  Label com contexto
        panel: panelId,
        col: 0,
        row: currentRow, //  Empilhar verticalmente
        colSpan: 24,
        dataPath: entity.dataPath,
        columns: entity.suggestedColumns,
        enablePagination: config?.enablePagination ?? true,
        pageSize: config?.pageSize ?? 10,
        enableSorting: config?.enableSorting ?? true,
        enableFilters: config?.enableFilters ?? true,
        enableCRUD: config?.enableCRUD ?? false,
        selectionMode: 'single',
        previewMode: true,
        //  NOVA LÓGICA: Master-Detail pode estar ativo MESMO sem formulários gerados
        // O usuário pode adicionar campos manualmente depois
        enableMasterDetail: config?.enableMasterDetail ?? false,
        masterDetailFormPanel: formPanelId || null // Se null, usuário pode configurar depois
      };

      

      //  Adicionar DataGrid PRIMEIRO
      fields.push(gridField);
      
      //  Depois adicionar campos do formulário
      fields.push(...formFields);
    });



    return {
      tabs,
      panels,
      fields
    };
  }

  /**
   * Cria um field a partir de um AnalyzedField
   */
  private createFieldFromAnalysis(field: any, panelId: string, row: number): any {
    const baseField: any = {
      field: `field_${field.name}_${Date.now()}`,
      type: field.type,
      label: field.label,
      placeholder: `Digite ${field.label.toLowerCase()}...`,
      panel: panelId,
      col: 0,
      row: row,
      colSpan: field.type === 'textarea' ? 24 : 12,
      required: false,
      dataPath: field.dataPath
    };

    // Props específicas por tipo
    if (field.type === 'number') {
      Object.assign(baseField, {
        min: 0,
        max: 9999999,
        step: 1,
        precision: 0
      });
    } else if (field.type === 'date') {
      Object.assign(baseField, {
        format: 'dd/MM/yyyy'
      });
    } else if (field.type === 'boolean') {
      baseField.type = 'switch'; // Usar switch ao invés de boolean
      Object.assign(baseField, {
        checkedChildren: 'Sim',
        unCheckedChildren: 'Não',
        defaultValue: false
      });
    } else if (field.type === 'textarea') {
      Object.assign(baseField, {
        rows: 4,
        minRows: 4
      });
    }

    return baseField;
  }

  /**
   * Infere tipo de coluna da grid baseado no valor
   */
  private inferGridColumnType(value: any): 'text' | 'number' | 'date' | 'boolean' {
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'boolean';
    if (value instanceof Date || (typeof value === 'string' && /\d{4}-\d{2}-\d{2}/.test(value))) return 'date';
    return 'text';
  }

  /**
   * Formata label (reutilizado do ScreenAnalyzerService)
   */
  private formatLabel(fieldName: string): string {
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
      .trim();
  }

  /**
   *  NOVO: Mapeia tipo de coluna do DataGrid para tipo de componente de formulário
   */
  private mapColumnTypeToFormType(columnType: string): string {
    const typeMap: Record<string, string> = {
      'text': 'text',
      'number': 'number',
      'date': 'date',
      'boolean': 'switch',
      'email': 'text',
      'phone': 'text',
      'url': 'text'
    };
    return typeMap[columnType] || 'text';
  }

  /**
   *  NOVO: Retorna props padrão baseado no tipo do componente de formulário
   */
  private getDefaultPropsForFormType(componentType: string): any {
    const defaults: Record<string, any> = {
      'number': {
        min: null,
        max: null,
        step: 1,
        precision: 0
      },
      'date': {
        dateFormat: 'DD/MM/YYYY',
        showTime: false
      },
      'switch': {
        defaultValue: false,
        checkedChildren: 'Sim',
        unCheckedChildren: 'Não'
      },
      'text': {
        maxLength: 255
      },
      'textarea': {
        rows: 4,
        maxLength: 1000
      }
    };
    return defaults[componentType] || {};
  }
}


